import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { useParams } from "react-router";
import { TextField, Box, Grid, Backdrop } from "@material-ui/core";
import { makeStyles, Theme } from "@material-ui/core/styles";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import { downloadFile } from "src/app-redux/common-actions/index";
import { useAppDispatch } from "src/app-redux/hooks";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { uploadDocument, getAllGateDocuments } from "src/app-redux/vehicle/actions/vehicleActions";
import { IAddGateEntery } from "src/common/typings/vehicle";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonButton from "src/components/CommonButton/CommonButton";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import { getAllFileCompressionConfigs } from "src/app-redux/configuration/actions/configurationActions";
import Tooltip from "@mui/material/Tooltip";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonLoader from "src/components/Loader/CommonLoader";
const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
  },
  heading: {
    fontSize: theme.typography.pxToRem(17),
    flexBasis: "33.33%",
    flexShrink: 0,
    lineHeight: "28px",
  },
  tabPanelHead: {
    display: "flex",
    width: "100%",
    margin: "0px 0 0px",
    justifyContent: "space-between",
    alignItems: "center",
    fontFamily: "Inter",
  },
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
  },
  formControl: {
    width: "100%",
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  padding: {
    padding: "10px 5px 0 5px",
  },
}));

export interface IForm extends IAddGateEntery {}

interface Props {
  submitCounter: number;
  fileTypes: any;
  getAllGateDocs: any;
  gatePassId: any;
}

export function DocumentsInputs({ fileTypes, getAllGateDocs }: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [docInvoice, setDocInvoice] = useState<boolean>(false);
  const [loading, setLoading] = React.useState(false);
  const { id } = useParams<{ id: any }>();
  const [fileToUpload, setFileToUpload] = React.useState<any>();

  const toolkitCommment = (param: any) => (
    <Tooltip title={param.value} followCursor>
      <span className="csutable-cell-trucate">{param.value}</span>
    </Tooltip>
  );

  const columns = [
    {
      accessorKey: "fileCategory",
      header: "File Type",
      type: "string",
      flex: 1,
    },
    {
      accessorKey: "originalFileName",
      header: "Title",
      type: "string",
      flex: 1,
    },
    {
      accessorKey: "comment",
      header: "Remarks",
      type: "string",
      flex: 1,
      renderCell: toolkitCommment,
    },
    {
      accessorKey: "actions",
      header: "Actions",
      type: "string",
      flex: 1,
      accessorFn: (row: any) => ` ${row.actions || " "}`,
    },
  ];
  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);

  const [validFileTypes, setValidFileTypes] = React.useState<any>([]);
  const [validFileSize, setValidFileSize] = React.useState(0);
  const [isValidFileType, setIsValidFileType] = React.useState<boolean>(true);
  const [isValidFileSize, setIsValidFileSize] = React.useState<boolean>(true);
  let fileTypesArr: any[] = [];
  let maxFileSize = 0;

  useEffect(() => {
    getFileConfig();
    if (getAllGateDocs.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        downloadDocument(row.original);
                      }}
                      icon={<CloudDownloadIcon />}
                      isEdit={true}
                    >
                      Download
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column
      );
      setDisplayColumns(transformedColumns);
    }
  }, [getAllGateDocs]);

  const getFileConfig = async () => {
    const res = await dispatch(getAllFileCompressionConfigs());
    if (res.payload) {
      setValidFileTypes(res.payload[0]?.fileTypes.split(","));
      setValidFileSize(res.payload[0]?.fileSizeThreshold);
      fileTypesArr = res.payload[0]?.fileTypes.split(",");
      maxFileSize = res.payload[0]?.fileSizeThreshold;
    }
  };

  const getbtnStatus = () => {
    let status = false;
    if (
      fileToUpload &&
      fileCategoryId &&
      isValidFileSize &&
      isValidFileType &&
      fileComment.length < 3000
    ) {
      if (fileCategoryId === "Vendor Invoice" && docInvoice) {
        status = true;
      } else {
        status = false;
      }
    } else {
      status = true;
    }
    return status;
  };
  const submitDocForm = async () => {
    setLoading(true);
    if (fileToUpload !== undefined) {
      const res: any = await dispatch(
        uploadDocument({
          url: "",
          contentType: fileToUpload?.type,
          size: fileToUpload?.size,
          fileRecord: fileToUpload,
          fileCategoryId: fileCategoryId,
          gatePassId: id,
          fileComment: fileComment,
        })
      );
      if (res?.payload === true) {
        dispatch(getAllGateDocuments({ gatePassId: id }));
        setFileToUpload(null);
        setfileCategoryId("");
        setfileComment("");
      }
    } else {
      dispatch(
        addNotificationAction({
          message: "Please select a file to upload",
          options: { variant: "error" },
        })
      );
    }

    setLoading(false);
  };

  const onDrop = React.useCallback((acceptedFiles: File[]) => {
    const split = acceptedFiles[0].name.split(".");

    setIsValidFileType(fileTypesArr.includes(`.${split[split.length - 1]}`.toLowerCase()));
    setIsValidFileSize(acceptedFiles[0].size / (1024 * 1024) <= maxFileSize);

    setFileToUpload(acceptedFiles[0]);
  }, []);
  const [fileCategoryId, setfileCategoryId] = React.useState<string>("");
  const [fileComment, setfileComment] = React.useState<string>("");
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const downloadDocument = async (row: any) => {
    const type =
      row.fileCategory === "Scan"
        ? 2
        : row.fileCategory === "Weight"
        ? 3
        : row.fileCategory === "Commercial"
        ? 4
        : 1;
    const fileMetaData = {
      url: `${process.env.REACT_APP_API_URL}/get-gate-doc-by-id/${row.id}?docType=${type}`,
      contentType: ".jpg",
      fileName: row.title,
    };
    dispatch(downloadFile(fileMetaData));
  };
  return (
    <>
      <form>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          <Box mt={2} />

          <Grid item xs={12} md={4} lg={3} className={classes.padding}>
            <Box>
              <CommonAutoCompleteField
                cssClassName="customTextField"
                label="Type"
                placeholder="Select Type"
                name="type"
                optionsType={fileTypes}
                required
                getOptionLabel={(option: any) => option.name || ""}
                value={fileTypes.find((ty: any) => ty.id === fileCategoryId) || null}
                onChange={(option: any) => setfileCategoryId(option?.id || "")}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4} lg={5} className={classes.padding}>
            <Box>
              <TextField
                multiline
                rows={1}
                rowsMax={5}
                className="customTextField"
                size="small"
                fullWidth
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                label="Remarks"
                name="comment"
                placeholder="Enter Remarks"
                value={fileComment}
                disabled={false}
                onChange={(e) => {
                  setfileComment(e.target.value);
                }}
                error={fileComment.length > 3000}
                helperText={fileComment.length > 3000 && "Maximum 3000 characters are allowed"}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4} lg={3} className={classes.padding}>
            <Box
              {...getRootProps()}
              p={0}
              pl={2}
              border={"1px solid rgb(196 196 196)"}
              borderRadius={5}
              height={38}
              pb={4}
              overflow={"hidden"}
              borderColor={isDragActive ? "error.main" : "rgb(196 196 196)"}
            >
              <input {...getInputProps()} />

              {fileToUpload ? (
                <p style={{ marginTop: 0 }}> {fileToUpload.name}</p>
              ) : isDragActive ? (
                <p>Drop a file here ...</p>
              ) : (
                <p style={{ color: "rgb(181 178 178)", marginTop: 9, paddingBottom: 10 }}>
                  {/* Drag and drop file here,or click to select a file */}
                  Upload File
                </p>
              )}
            </Box>
            <Box>
              <div style={{ color: "#f44336", marginBottom: 0 }}>
                <ul
                  style={{
                    paddingLeft: "16px",
                    margin: 0,
                    display: isValidFileType ? "none" : "block",
                  }}
                >
                  <li>
                    Only{" "}
                    {validFileTypes?.map((type: any, index: number) => {
                      return index < validFileTypes.length - 1 ? `${type},` : type;
                    })}{" "}
                    files are allowed.
                  </li>
                </ul>
                <ul
                  style={{
                    paddingLeft: "16px",
                    margin: 0,
                    display: isValidFileSize ? "none" : "block",
                  }}
                >
                  <li>File size cannot exceed {validFileSize}MB.</li>
                </ul>
              </div>
            </Box>
          </Grid>
          <Grid item xs={12} lg={1} className={classes.padding} style={{ marginTop: "9px" }}>
            <Box component="span" style={{ float: "right" }}>
              <CommonButton
                isGreen
                cssClass="customSubmitBtn"
                disable={getbtnStatus()}
                onClick={() => {
                  submitDocForm();
                }}
              >
                Save
              </CommonButton>
            </Box>
          </Grid>

          <Grid item xs={12} style={{ marginTop: "20px" }}>
            <CommonMaterialGrid
              defaultPerPageRecord={10}
              height={400}
              rows={getAllGateDocs || []}
              columns={displayColumns}
            />
          </Grid>
        </Grid>
      </form>
    </>
  );
}
